import React, { useState, useEffect } from 'react';
import StatusPill from './ui/StatusPill.jsx';
import Skeleton from './ui/Skeleton.jsx';
import VitalSigns from './show/VitalSigns.jsx';
import DailyMeetings from './show/DailyMeetings.jsx';
import FeedbackTimeline from './show/FeedbackTimeline.jsx';
import TeamLists from './show/TeamLists.jsx';
import DocsAndRepo from './show/DocsAndRepo.jsx';
import TimeTracking from './show/TimeTracking.jsx';
import HoursBudget from './show/HoursBudget.jsx';
import JumpNav from './show/JumpNav.jsx';
import HeaderUserChip from './ui/HeaderUserChip.jsx';
import HeaderBrand from './ui/HeaderBrand.jsx';

export default function ProjectShowPage(props) {
  const localProps = props || {};
  const currentUser = localProps.current_user || null;

  // Redmine-backed data loads after mount. null = still loading.
  const [redmine, setRedmine] = useState(null);
  const [redmineError, setRedmineError] = useState(false);
  // Bumped by "Sync with Redmine" so the Hours budget block also refetches fresh data.
  const [syncCount, setSyncCount] = useState(0);
  const loadingRedmine = redmine === null && !redmineError;

  // localProps is fixed at mount (server-embedded island props), so id never changes.
  // The mount load reads the server's 15-min per-project cache; `force` (the
  // "Sync with Redmine" button) bypasses it for fresh Redmine data.
  function loadRedmine(force = false) {
    setRedmineError(false);
    setRedmine(null);
    fetch(`/projects/${localProps.id}/redmine${force ? '?refresh=1' : ''}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((res) => {
        if (res.status === 401) { window.location = '/session/new'; return null; }
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((data) => { if (data) setRedmine(data); })
      .catch(() => setRedmineError(true));
  }

  useEffect(() => { loadRedmine(); }, []);

  // Local data is present immediately; Redmine fields fill in once fetched.
  const project = { ...localProps, ...(redmine || {}) };

  // Feedback entries state lives here so VitalSigns (unresolved count) and
  // FeedbackTimeline (list + actions) stay in sync without prop drilling refresh.
  const [feedbackEntries, setFeedbackEntries] = useState(localProps.feedback_entries || []);

  function onFeedbackSaved(saved, wasEdit) {
    if (wasEdit) {
      setFeedbackEntries((prev) => prev.map((e) => (e.id === saved.id ? saved : e)));
    } else {
      setFeedbackEntries((prev) => [saved, ...prev]);
    }
  }

  function onFeedbackDeleted(id) {
    setFeedbackEntries((prev) => prev.filter((e) => e.id !== id));
  }

  return (
    <div style={{ background: '#FAFAF7', minHeight: '100vh' }}>
      <header className="border-b" style={{ background: '#fff', borderColor: '#ECEBE5' }}>
        <div className="max-w-[1440px] mx-auto px-8 h-14 flex items-center justify-between">
          <HeaderBrand />
          <HeaderUserChip user={currentUser} />
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-8 pt-6 pb-24">
        <a href="/" className="text-[12px]" style={{ color: '#6B6B68', textDecoration: 'none' }}>← Projects</a>

        <div className="mt-3 flex items-center gap-3 mb-2">
          <h1 className="text-[28px] font-semibold tracking-tight" style={{ color: '#1A1A1A', letterSpacing: '-0.02em' }}>
            {loadingRedmine ? <Skeleton width={240} height={30} /> : (redmineError ? '—' : project.name)}
          </h1>
          {loadingRedmine
            ? <Skeleton width={60} height={20} radius={999} />
            : (redmineError ? null : <StatusPill status={project.status} />)}
        </div>

        <div className="mb-6">
          {redmineError && (
            <div className="text-[12px]" style={{ color: '#9B1F26' }}>
              Couldn't load live project data —{' '}
              <button
                type="button"
                onClick={() => loadRedmine(true)}
                style={{ background: 'none', border: 'none', padding: 0, color: '#9B1F26', textDecoration: 'underline', cursor: 'pointer' }}
              >Retry</button>
            </div>
          )}
        </div>

        <div className="grid" style={{ gridTemplateColumns: '1fr 180px', columnGap: 56 }}>
          <div>
            <VitalSigns
              project={project}
              loadingRedmine={loadingRedmine}
              redmineError={redmineError}
              onSyncRedmine={() => { loadRedmine(true); setSyncCount((n) => n + 1); }}
            />
            <DailyMeetings project={project} />
            <FeedbackTimeline
              project={project}
              entries={feedbackEntries}
              onSaved={onFeedbackSaved}
              onDeleted={onFeedbackDeleted}
            />
            <TeamLists project={project} loadingRedmine={loadingRedmine} redmineError={redmineError} />
            <TimeTracking project={project} loadingRedmine={loadingRedmine} redmineError={redmineError} />
            <HoursBudget project={project} refreshKey={syncCount} />
            <DocsAndRepo project={project} />
          </div>
          <aside style={{ paddingTop: 27 }}>
            <JumpNav project={project} />
          </aside>
        </div>
      </main>
    </div>
  );
}
